// src/components/layout/NavPatientSearch.tsx
// -----------------------------------------------------------------------------
// Patient typeahead for the top bar, shown on the patient-details path.
//
// Joan, 2026-09-10: the first version was a text field that did nothing until
// Enter and then navigated to the dashboard. That is a redirect wearing a
// search box — you type, nothing happens, and the page you were reading
// disappears. The point of putting search HERE is to go from one patient to
// another without leaving patient context, which means results have to appear
// under the cursor and be clickable.
//
// It calls the SAME endpoint and the same debounce as the dashboard search
// (/patients/?search=, 300ms), so there is one search behaviour in the product
// and this is a second surface onto it, not a second implementation. Names go
// through anonymizeName exactly as the dashboard list does; this control never
// reveals more than the list it mirrors.
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";

import api from "api/api";
import { athenaColors } from "../../theme/palette";
import { glassControl, glassInputSx } from "../../theme/glassTokens";
import { anonymizeName } from "../PatientDetails/panels/DataPanel";
import { useTranslation } from "../../i18n";
import logger from "utils/logger";

const MAX_RESULTS = 8;
const DEBOUNCE_MS = 300;

interface PatientHit {
  id: number;
  name?: string;
  environment_names?: string[];
}

export default function NavPatientSearch() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<PatientHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const lastQueryRef = useRef("");

  const run = useCallback(async (raw: string) => {
    const trimmed = raw.trim();
    lastQueryRef.current = trimmed;
    if (!trimmed) {
      abortRef.current?.abort();
      setHits([]);
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const resp = await api.get<{ count: number; results: PatientHit[] }>(
        `/patients/?search=${encodeURIComponent(trimmed)}&page_size=${MAX_RESULTS}`,
        { signal: ctrl.signal },
      );
      // A newer keystroke has already started: drop this response.
      if (lastQueryRef.current !== trimmed) return;
      setHits(resp.data.results ?? []);
      setLoading(false);
    } catch (error: unknown) {
      // Aborts happen on every fast keystroke and are not failures.
      const name = (error as { name?: string; code?: string })?.name;
      const code = (error as { name?: string; code?: string })?.code;
      if (name === "CanceledError" || code === "ERR_CANCELED") return;
      logger.error(error);
      if (lastQueryRef.current === trimmed) setLoading(false);
    }
  }, []);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    },
    [],
  );

  const onChange = (value: string) => {
    setQuery(value);
    setHighlight(-1);
    setOpen(true);
    setLoading(value.trim().length > 0);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => run(value), DEBOUNCE_MS);
  };

  const go = (id: number) => {
    setOpen(false);
    setQuery("");
    setHits([]);
    navigate(`/patients/${id}/details`);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!hits.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((p) => (p < hits.length - 1 ? p + 1 : p));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((p) => (p > 0 ? p - 1 : -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(hits[highlight >= 0 ? highlight : 0].id);
    }
  };

  const showPanel = open && query.trim().length > 0;

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box
        ref={anchorRef}
        sx={{
          flex: 1,
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          px: 3,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 720 }}>
          <TextField
            value={query}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => setOpen(true)}
            placeholder={t("nav.patient_search", "Search patients...")}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon
                    sx={{ fontSize: 18, color: athenaColors.periwinkle }}
                  />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress
                    size={14}
                    sx={{ color: athenaColors.periwinkle }}
                  />
                </InputAdornment>
              ) : undefined,
            }}
            sx={{
              ...glassInputSx,
              "& .MuiOutlinedInput-root": {
                ...glassInputSx["& .MuiOutlinedInput-root"],
                height: 40,
                borderRadius: glassControl.radius,
                fontSize: "0.875rem",
              },
            }}
          />

          <Popper
            open={showPanel}
            anchorEl={anchorRef.current}
            placement="bottom"
            style={{ zIndex: theme.zIndex.modal }}
          >
            <Paper
              elevation={8}
              sx={{
                mt: 0.5,
                width: anchorRef.current?.clientWidth
                  ? Math.min(anchorRef.current.clientWidth - 48, 720)
                  : 600,
                maxHeight: 380,
                overflowY: "auto",
                borderRadius: 2,
                backgroundColor: alpha(athenaColors.deepNavy, 0.96),
                backgroundImage: "none",
                border: `1px solid ${alpha(theme.palette.common.white, 0.14)}`,
                color: theme.palette.common.white,
              }}
            >
              {!loading && hits.length === 0 && (
                <Typography
                  variant="body2"
                  sx={{
                    px: 2,
                    py: 1.5,
                    textAlign: "center",
                    color: alpha(theme.palette.common.white, 0.65),
                  }}
                >
                  {t("patients.no_results", "No patients found")}
                </Typography>
              )}
              {hits.map((h, idx) => (
                <Box
                  key={h.id}
                  onClick={() => go(h.id)}
                  onMouseEnter={() => setHighlight(idx)}
                  sx={{
                    px: 2,
                    py: 1,
                    cursor: "pointer",
                    borderBottom: `1px solid ${alpha(
                      theme.palette.common.white,
                      0.07,
                    )}`,
                    "&:last-child": { borderBottom: "none" },
                    backgroundColor:
                      idx === highlight
                        ? alpha(athenaColors.periwinkle, 0.18)
                        : "transparent",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "0.88rem", fontWeight: 600, lineHeight: 1.4 }}
                  >
                    {h.name
                      ? anonymizeName(h.name)
                      : t("dashboard.unnamed_patient", "Unnamed Patient")}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: alpha(theme.palette.common.white, 0.6),
                    }}
                  >
                    ID: {h.id}
                    {h.environment_names?.length
                      ? ` · ${h.environment_names.join(", ")}`
                      : ""}
                  </Typography>
                </Box>
              ))}
            </Paper>
          </Popper>
        </Box>
      </Box>
    </ClickAwayListener>
  );
}
